import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";

import Providers from "@/context/Providers";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/auth/AdminProtectedRoute";
import { BillingProtectedRoute } from "@/components/auth/BillingProtectedRoute";
import { RequireOrg } from "@/components/auth/RequireOrg";
import { RootRoute } from "@/components/auth/RootRoute";
import { useHttpsRedirect } from "@/hooks/useHttpsRedirect";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OnboardingPage from "@/pages/OnboardingPage";
import BillingPage from "@/pages/BillingPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import AutomationServersPage from "@/pages/AutomationServersPage";
import AutomationServerDetailPage from "@/pages/AutomationServerDetailPage";
import SettingsPage from "@/pages/SettingsPage";
import AccountPage from "@/pages/AccountPage";

import AdvisorPortalLayout from "@/components/advisor/layout/AdvisorPortalLayout";
import AdvisorDashboardPage from "@/pages/advisor/DashboardPage";
import AdvisorProfilePage from "@/pages/advisor/ProfilePage";
import AdvisorClientsPage from "@/pages/advisor/ClientsPage";
import AdvisorBlogManagementPage from "@/pages/advisor/BlogManagementPage";
import AdvisorPublicOffersPage from "@/pages/advisor/PublicOffersPage";
import AdvisorMailingListPage from "@/pages/advisor/MailingListPage";
import AdvisorMessagesPage from "@/pages/advisor/MessagesPage";
import AdvisorTeamPage from "@/pages/advisor/TeamPage";
import AdvisorOrgSettingsPage from "@/pages/advisor/OrgSettingsPage";
import ClientOverviewPage from "@/pages/advisor/client/ClientOverviewPage";
import ClientInfoPage from "@/pages/advisor/client/ClientInfoPage";
import ClientServersPage from '@/pages/advisor/client/ClientServersPage';
import ClientUsersPage from '@/pages/advisor/client/ClientUsersPage';
import ClientOffersPage from '@/pages/advisor/client/ClientOffersPage';
import ClientTimePage from '@/pages/advisor/client/ClientTimePage';
import ClientBillingPage from '@/pages/advisor/client/ClientBillingPage';
import AdvisorBillingPage from '@/pages/advisor/BillingPage';
import ContractsPage from '@/pages/ContractsPage';
import AdvisorClientScope from '@/components/advisor/client/AdvisorClientScope';
import PublicDirectoryPage from "@/pages/advisor/PublicDirectoryPage";
import BecomeAnAdvisorPage from '@/pages/advisor/BecomeAnAdvisorPage';
import AdvisorOnboardingPage from '@/pages/advisor/AdvisorOnboardingPage';
import AdvisorCertificationPage from '@/pages/advisor/CertificationPage';
import FindAdvisorPage from '@/pages/FindAdvisorPage';
import PublicProfilePage, { PublicBlogPostPage } from "@/pages/advisor/PublicProfilePage";


function App() {
    // Redirect to HTTPS if currently on HTTP (production only)
    useHttpsRedirect();

    return (
        <Providers>
            <Router>
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />

                    <Route
                        path="/onboarding"
                        element={
                            <ProtectedRoute>
                                <OnboardingPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/advisor-onboarding"
                        element={
                            <ProtectedRoute>
                                <AdvisorOnboardingPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* An advisor's public presence. Unauthenticated: this is
                        the catalogue a prospective client browses to choose who
                        they want to work with. */}
                    <Route path="/public/advisors" element={<PublicDirectoryPage />} />
                    <Route path="/become-an-advisor" element={<BecomeAnAdvisorPage />} />
                    <Route path="/public/advisor/:profileId" element={<PublicProfilePage />} />
                    <Route path="/public/advisor/:profileId/blog/:slug" element={<PublicBlogPostPage />} />

                    <Route
                        path="/advisor"
                        element={
                            <ProtectedRoute>
                                <AdvisorPortalLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/advisor/dashboard" replace />} />
                        <Route path="dashboard" element={<AdvisorDashboardPage />} />
                        <Route path="profile" element={<AdvisorProfilePage />} />
                        <Route path="blog" element={<AdvisorBlogManagementPage />} />
                        <Route path="public-offers" element={<AdvisorPublicOffersPage />} />
                        <Route path="mailing-list" element={<AdvisorMailingListPage />} />
                        <Route path="messages" element={<AdvisorMessagesPage />} />
                        <Route path="certification" element={<AdvisorCertificationPage />} />
                        <Route path="billing" element={<AdvisorBillingPage />} />
                        <Route path="team" element={<AdvisorTeamPage />} />
                        <Route path="org-settings" element={<AdvisorOrgSettingsPage />} />
                        <Route path="clients" element={<AdvisorClientsPage />} />
                        <Route path="clients/:id" element={<AdvisorClientScope />}>
                            <Route index element={<Navigate to="overview" replace />} />
                            <Route path="overview" element={<ClientOverviewPage />} />
                            <Route path="info" element={<ClientInfoPage />} />
                            <Route path="servers" element={<ClientServersPage />} />
                            <Route path="users" element={<ClientUsersPage />} />
                            <Route path="offers" element={<ClientOffersPage />} />
                            <Route path="time" element={<ClientTimePage />} />
                            <Route path="billing" element={<ClientBillingPage />} />
                        </Route>
                    </Route>

                    <Route
                        path="/auth/callback"
                        element={<AuthCallbackPage />}
                    />

                    {/* Protected routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <RequireOrg>
                                    <DashboardLayout path="/dashboard" />
                                </RequireOrg>
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<DashboardPage />} />
                        <Route path="*" element={<DashboardPage />} />
                    </Route>

                    {/* Automation server routes */}
                    <Route
                        path="/automation-servers"
                        element={
                            <AdminProtectedRoute>
                                <RequireOrg>
                                    <DashboardLayout path="/automation-servers" />
                                </RequireOrg>
                            </AdminProtectedRoute>
                        }
                    >
                        <Route index element={<AutomationServersPage />} />
                        <Route
                            path=":id"
                            element={<AutomationServerDetailPage />}
                        />
                    </Route>

                    <Route
                        path="/account"
                        element={
                            <ProtectedRoute>
                                <DashboardLayout path="/account" />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<AccountPage />} />
                    </Route>

                    <Route
                        path="/contracts"
                        element={
                            <ProtectedRoute>
                                <RequireOrg>
                                    <DashboardLayout path="/contracts" />
                                </RequireOrg>
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<ContractsPage />} />
                    </Route>

                    <Route
                        path="/billing"
                        element={
                            <BillingProtectedRoute>
                                <RequireOrg>
                                    <DashboardLayout path="/billing" />
                                </RequireOrg>
                            </BillingProtectedRoute>
                        }
                    >
                        <Route index element={<BillingPage />} />
                    </Route>

                    <Route
                        path="/find-an-advisor"
                        element={
                            <ProtectedRoute>
                                <RequireOrg>
                                    <DashboardLayout path="/find-an-advisor" />
                                </RequireOrg>
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<FindAdvisorPage />} />
                    </Route>

                    {/* Settings route */}
                    <Route
                        path="/settings"
                        element={
                            <AdminProtectedRoute>
                                <DashboardLayout path="/settings" />
                            </AdminProtectedRoute>
                        }
                    >
                        <Route index element={<SettingsPage />} />
                    </Route>

                    <Route path="/" element={<RootRoute />} />
                    <Route
                        path="*"
                        element={<Navigate to="/dashboard" replace />}
                    />
                </Routes>
            </Router>

            <Toaster richColors closeButton position="top-right" />

        </Providers>
    );
}

export default App;
